import { CheckCircle2, ChevronLeft, FileUp, FolderInput, Gavel, MessageSquareShare } from "lucide-react";
import { type ReactNode, useState } from "react";

import { FilePreviewDialog, type PreviewFile } from "@/components/files/file-preview-dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { notify } from "@/lib/notify";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useAddProcedureDocument, useGeneratePleadingBrief } from "../../api/use-claim-actions";
import { groupAndPrepareExhibits } from "../exhibits/exhibit-utils";
import { ExhibitsList } from "../exhibits/exhibits-list";

type PleadingBriefSource = "LEGAL_SUBPOENA_SERVED" | "RESPONSE_CONCLUSIONS" | "UPLOAD";

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

interface SourceOptionProps {
  value: PleadingBriefSource;
  label: string;
  icon: ReactNode;
  disabled?: boolean;
  selected: boolean;
  children: ReactNode;
}

/** Radio card — mirrors RadioGroupCardOption (Vue). */
function SourceOption({ value, label, icon, disabled, selected, children }: SourceOptionProps) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md border p-4",
        selected ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600" : "border-gray-200 bg-white",
        disabled ? "cursor-not-allowed opacity-60" : "hover:border-gray-300",
      )}
    >
      <RadioGroupItem id={`pleading-source-${value}`} value={value} disabled={disabled} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <label
          htmlFor={`pleading-source-${value}`}
          className={cn(
            "flex items-center gap-2 text-sm font-medium text-gray-900",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
          )}
        >
          {icon}
          {label}
        </label>
        <div className="mt-1 text-sm text-gray-500">{children}</div>
      </div>
    </div>
  );
}

/** Port of states-cta/GeneratePleadingBriefCTA.vue — 2-step: source doc, then exhibits. */
export function GeneratePleadingBriefCTA({ claim }: { claim: ClaimWithRelations }) {
  const procedure = claim.procedure;

  // Find the served legal subpoena document
  const legalSubpoenaServed = claim.claimDocuments?.find(
    (doc) => doc.documentType.type === "LEGAL_SUBPOENA_SERVED",
  );

  // Find the last RESPONSE_CONCLUSIONS from procedure documents
  const lastResponseConclusions =
    procedure?.procedureDocuments
      ?.filter((doc) => doc.type?.name === "RESPONSE_CONCLUSIONS")
      .sort(
        (a, b) => new Date(b.date || b.createdAt).getTime() - new Date(a.date || a.createdAt).getTime(),
      )[0] ?? null;

  const defaultSource: PleadingBriefSource = legalSubpoenaServed
    ? "LEGAL_SUBPOENA_SERVED"
    : lastResponseConclusions
      ? "RESPONSE_CONCLUSIONS"
      : "UPLOAD";

  const [selectedSource, setSelectedSource] = useState<PleadingBriefSource>(defaultSource);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [previewedFile, setPreviewedFile] = useState<PreviewFile | null>(null);

  const addProcedureDocument = useAddProcedureDocument(claim.id);
  const generatePleadingBriefMutation = useGeneratePleadingBrief(claim.id);
  const isLoading = addProcedureDocument.isPending || generatePleadingBriefMutation.isPending;

  // Process claim documents and extract exhibit numbers
  const exhibitData = groupAndPrepareExhibits(
    (claim.claimDocuments || []).map((doc) => ({
      id: doc.id,
      file: doc.file ? { id: doc.file.id, name: doc.file.name || "", boxFileId: doc.file.boxFileId } : null,
    })),
  );

  function previewDocument(file: { id: string; name: string } | null) {
    if (!file) {
      return;
    }
    setPreviewedFile({ id: file.id, name: file.name, mimeType: "application/pdf", size: null });
  }

  function goToStep2() {
    if (selectedSource === "UPLOAD" && !uploadedFile) {
      notify.error("Veuillez sélectionner un fichier");
      return;
    }
    setCurrentStep(2);
  }

  async function generatePleadingBrief() {
    let conclusionsFileId: string | undefined;

    // If uploading a new file, first add it as a procedure document
    if (selectedSource === "UPLOAD" && uploadedFile && procedure?.id) {
      const response = await addProcedureDocument.mutateAsync({
        name: uploadedFile.name,
        document: await readFileAsDataUrl(uploadedFile),
        mimeType: uploadedFile.type,
        procedureDocumentType: "RESPONSE_CONCLUSIONS",
        procedureId: procedure.id,
        source: "MANUAL",
      });
      conclusionsFileId = response.file?.id;
    } else if (selectedSource === "RESPONSE_CONCLUSIONS" && lastResponseConclusions) {
      conclusionsFileId = lastResponseConclusions.file?.id;
    } else if (selectedSource === "LEGAL_SUBPOENA_SERVED" && legalSubpoenaServed) {
      conclusionsFileId = legalSubpoenaServed.file?.id;
    }

    // Prepare ordered exhibits to send to backend
    const orderedExhibits = exhibitData.toStamp.flatMap((doc) =>
      doc.exhibit ? [{ claimDocumentId: doc.id, exhibitNumber: doc.exhibit.main }] : [],
    );

    const { sharedLink } = await generatePleadingBriefMutation.mutateAsync({
      sourceDocumentFileId: conclusionsFileId,
      sourceType: selectedSource,
      orderedExhibits,
    });
    window.open(sharedLink, "_blank");
  }

  return (
    <>
      <FilePreviewDialog
        open={Boolean(previewedFile)}
        file={previewedFile}
        onClose={() => setPreviewedFile(null)}
      />

      <div className="border-b bg-slate-50">
        <div className="mx-auto max-w-xl py-5 text-left">
          <div className="rounded-md border bg-white p-6 shadow">
            <div className="mb-4 flex items-center gap-3">
              <div className="rounded-full bg-blue-100 p-2">
                <FolderInput className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-base leading-6 font-semibold text-gray-900">
                  Générer le dossier de plaidoirie
                </h2>
                <p className="text-sm text-gray-500">
                  {currentStep === 1
                    ? "Sélectionnez le document source pour la génération"
                    : "Vérifiez les pièces à tamponner"}
                </p>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
              <span className={currentStep === 1 ? "font-medium text-blue-600" : ""}>1. Source</span>
              <span>→</span>
              <span className={currentStep === 2 ? "font-medium text-blue-600" : ""}>2. Pièces</span>
            </div>

            {currentStep === 1 ? (
              <>
                <RadioGroup
                  value={selectedSource}
                  onValueChange={(value) => setSelectedSource(value as PleadingBriefSource)}
                  className="gap-3"
                >
                  <SourceOption
                    value="LEGAL_SUBPOENA_SERVED"
                    label="Assignation notifiée"
                    icon={<Gavel className="h-4 w-4 text-gray-500" />}
                    disabled={!legalSubpoenaServed}
                    selected={selectedSource === "LEGAL_SUBPOENA_SERVED"}
                  >
                    {legalSubpoenaServed ? (
                      "Utiliser l'assignation notifiée comme base (aucune conclusion produite)"
                    ) : (
                      <span className="text-amber-600">Aucune assignation notifiée disponible</span>
                    )}
                  </SourceOption>

                  <SourceOption
                    value="RESPONSE_CONCLUSIONS"
                    label="Dernières conclusions (Tribunal Digital)"
                    icon={<MessageSquareShare className="h-4 w-4 text-gray-500" />}
                    disabled={!lastResponseConclusions}
                    selected={selectedSource === "RESPONSE_CONCLUSIONS"}
                  >
                    {lastResponseConclusions ? (
                      <>
                        {lastResponseConclusions.type?.displayText} -{" "}
                        {new Date(
                          lastResponseConclusions.date || lastResponseConclusions.createdAt,
                        ).toLocaleDateString("fr")}
                      </>
                    ) : (
                      <span className="text-amber-600">Aucune conclusion Tribunal Digital disponible</span>
                    )}
                  </SourceOption>

                  <SourceOption
                    value="UPLOAD"
                    label="Sélectionner la dernière conclusion"
                    icon={<FileUp className="h-4 w-4 text-gray-500" />}
                    selected={selectedSource === "UPLOAD"}
                  >
                    <p>Importer un fichier de conclusions (sera ajouté comme document de procédure)</p>
                    <label className="mt-2 inline-flex cursor-pointer items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50">
                      <FileUp className="h-4 w-4 text-gray-500" />
                      Sélectionner un fichier
                      <input
                        type="file"
                        className="hidden"
                        onChange={(event) => setUploadedFile(event.target.files?.[0] ?? null)}
                      />
                    </label>
                    {uploadedFile ? (
                      <p className="mt-2 flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        {uploadedFile.name}
                      </p>
                    ) : null}
                  </SourceOption>
                </RadioGroup>

                <div className="mx-auto mt-6 justify-center text-center">
                  <Button
                    onClick={goToStep2}
                    disabled={!selectedSource || (selectedSource === "UPLOAD" && !uploadedFile)}
                  >
                    Suivant
                  </Button>
                </div>
              </>
            ) : (
              <>
                <ExhibitsList exhibitData={exhibitData} onPreview={previewDocument} />

                <div className="mt-6 flex items-center justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    <ChevronLeft />
                    Précédent
                  </Button>
                  <Button
                    onClick={() => void generatePleadingBrief()}
                    disabled={isLoading || exhibitData.toStamp.length === 0}
                  >
                    <FolderInput />
                    Générer le dossier de plaidoirie
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
